import { Deal, OrderValues } from '@ca/common/types'
import useOrderValues from '@order/hooks/values/useOrderValues'
import { getValuesDefaults } from '@order/hooks/values/OrderValues'
import { useCallback, useMemo } from 'react'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import { OrderFrame } from '@order/orderTypes'
import FrameUtils from '@frame/FrameUtils'
import { ErpFrame } from '@ca/common'
import { debugConditions } from '@order/utils/getDebugLog'
import { useLastStore } from 'hooks/store/useLastValidStore'
import { mergeValues } from '@order/utils/OrderVariables'
import ConfigConditions from './ConfigConditions'

/**
 * Returns a function to check if a condition is met
 */
export const useConfigConditionCheck = ({
  frame,
}: { frame?: OrderFrame } = {}) => {
  const config = useRemoteConfig()
  const values = useOrderValues({ frame })
  const check = useCallback(
    (name: string) => {
      const condition = config?.configConditions?.[name]
      debugConditions.log('Checking config condition', {
        name,
        condition,
        values,
      })
      return ConfigConditions.check(condition, values, config?.conditions)
    },
    [values, config]
  )

  return check
}

/**
 * Returns a function to check if a condition is met
 * for a specific frame
 */
export const useConfigConditionFrameCheck = ({
  values,
}: {
  values: OrderValues
}) => {
  const config = useRemoteConfig()

  const check = useCallback(
    (name: string, frame: ErpFrame) => {
      const condition = config?.configConditions?.[name]

      const frameValues = FrameUtils.getValues(frame)

      const resolvedValues = {
        values: mergeValues(values.values, frameValues),
        refraction: values.refraction,
      }

      return ConfigConditions.check(
        condition,
        resolvedValues,
        config?.conditions
      )
    },
    [config, values]
  )

  return check
}

/**
 * Returns a function to check if a condition is met
 * for a specific Deal
 *
 */
export const useConfigConditionDealCheck = () => {
  const config = useRemoteConfig()
  const store = useLastStore()
  const check = useCallback(
    (name: string, deal: Deal) => {
      const condition = config?.configConditions?.[name]

      const values = getValuesDefaults({
        dealCode: deal.code,
        dealType: deal.type,
        frames: deal.frames,
        customer: undefined,
        store: store ?? undefined,
      }) as Record<string, string | number>

      return ConfigConditions.check(
        condition,
        {
          values,
          refraction: null,
        },
        config?.conditions
      )
    },
    [config, store]
  )

  return check
}

// Build group checkers
export const useConfigConditionGroupCheckBase = () => {
  const config = useRemoteConfig()

  const getGroup = useCallback(
    (group: string) => ConfigConditions.group.get(group, config),
    [config]
  )

  return { getGroup, checkGroup: ConfigConditions.group.check }
}

export const useConfigConditionGroupCheck = () => {
  const check = useConfigConditionCheck()

  const { getGroup, checkGroup } = useConfigConditionGroupCheckBase()

  return useCallback(
    (group: string) => {
      const toCheck = getGroup(group)
      return checkGroup(check, toCheck)
    },
    [getGroup, checkGroup, check]
  )
}

/**
 * Returns a boolean or undefined value checked against a specific condition
 */
export const useConfigCondition = (name: string) => {
  const check = useConfigConditionCheck()

  return useMemo(() => check(name), [check, name])
}
